import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as firebase from 'firebase/compat/app';
import { User } from './auth.service';
import { environment } from '../../environments/environment';
import { CouncilService } from './council.service';

import { ActivityData } from './data/activity.data';

@Injectable({
  providedIn: 'root'
})
export class ActivityService {

  readonly ACTIVITY_POINTS = ActivityData.data.activityPoints;
  readonly SUPREME_FEATURED_ACTIVITY_POINTS = ActivityData.data.supremeFeaturedProgramPoints;

  activitiesCollectionName = `activities-${environment.fraternalYear}`;
  activityDataCollectionName = `activityData-${environment.fraternalYear}`;
  councilGradingStatsCollectionName = `councilGradingStats-${environment.fraternalYear}`;

  constructor(private firestore: AngularFirestore,
              private councilService: CouncilService) { }

  get timestamp() {
    return firebase.default.firestore.FieldValue.serverTimestamp();
  }

  addActivity(activity: Activity) {
    console.log('Adding Activity');
    return new Promise<any>((resolve, reject) => {
      const batch = this.firestore.firestore.batch();
      const timestamp = this.timestamp;

      const activitiesDoc = this.firestore.firestore.collection(this.activitiesCollectionName).doc();
      batch.set(activitiesDoc, {
        ...activity,
        graded: false,
        updatedAt: timestamp,
        createdAt: timestamp
      });

      batch.commit().then(res => resolve(res), err => reject(err));
    });
  }

  getActivity(activityId: string) {
    console.log(`Getting Activity ${activityId}`);
    return this.firestore.collection(this.activitiesCollectionName).doc<Activity>(activityId).get();
  }

  getActivities(programGroupId?: string, graded?: boolean) {
    console.log('Getting Activities by Program Group ID: ' + programGroupId);
    return this.firestore.collection(this.activitiesCollectionName, ref => {
      let returnRef = ref.orderBy('councilId').orderBy('createdAt');
      returnRef = programGroupId == null ? returnRef : returnRef.where('programGroupId', '==', programGroupId);
      return graded == null ? returnRef : returnRef.where('graded', '==', graded);
    }).snapshotChanges();
  }

  getActivitiesForUser(user: User) {
    console.log(`Getting Activities for user: ${user.name}`);
    if (user.roles.admin) {
      return this.getActivities();
    } else if (user.roles.generalProgramDirector) {
      return this.getActivities(null, false);
    } else if (user.roles.communityDirector) {
      return this.getActivities('community', false);
    } else if (user.roles.faithDirector) {
      return this.getActivities('faith', false);
    } else if (user.roles.familyDirector) {
      return this.getActivities('family', false);
    } else if (user.roles.lifeDirector) {
      return this.getActivities('life', false);
    } else if (user.roles.membershipDirector) {
      return this.getActivities('membership', false);
    } else {
      return null;
    }
  }

  getCouncilActivities(councilId: string) {
    console.log('Getting Council Activities');
    return this.firestore.collection(this.activitiesCollectionName, ref => {
      return ref.where('councilId', '==', Number(councilId));
    }).snapshotChanges();
  }

  getActivityData(activityId: string) {
    console.log(`Getting Activity Data ${activityId}`);
    return this.firestore.collection(this.activityDataCollectionName).doc(activityId).snapshotChanges();
  }

  gradeActivity(activity: Activity, gradedActivity: GradedActivity) {
    console.log('Grading Activity');
    return new Promise<any>((resolve, reject) => {
      const batch = this.firestore.firestore.batch();
      const timestamp = this.timestamp;

      const activitiesDoc = this.firestore.firestore.collection(this.activitiesCollectionName).doc(gradedActivity.activityId);
      if (gradedActivity.activityStatus === 'reviewed') {
        // If this has only been reviewed, then it isn't actually graded
        batch.set(activitiesDoc, {graded: false}, {merge: true});
      } else {
        batch.set(activitiesDoc, {graded: true}, {merge: true});
      }
      
      const activityDataDoc = this.firestore.firestore.collection(this.activityDataCollectionName).doc(gradedActivity.activityId);

      // tslint:disable-next-line:max-line-length
      const activityPoints = (gradedActivity.activityStatus === 'rejected' || gradedActivity.activityStatus === 'reviewed') ? 0 : gradedActivity.isSupremeFeaturedProgram ? this.SUPREME_FEATURED_ACTIVITY_POINTS : this.ACTIVITY_POINTS;

      const activityData = {
        activityStatus: gradedActivity.activityStatus,
        activityPoints,
        isSupremeFeaturedProgram: gradedActivity.isSupremeFeaturedProgram,
        programDirectorComments: gradedActivity.programDirectorComments,
        isNotable: gradedActivity.isNotable,
        updatedAt: timestamp,
        createdAt: timestamp
      };

      batch.set(activityDataDoc, activityData, {merge: true});

      const council = this.councilService.getCouncilById(activity.councilId);
      const councilGradingStatsCol = this.firestore.firestore.collection(this.councilGradingStatsCollectionName);
      const councilGradingStatsDoc = councilGradingStatsCol.doc(`${council.id}`);

      const councilStats = {
        [`${activity.activityTypeId}-graded`]: firebase.default.firestore.FieldValue.increment(1),
        updatedAt: timestamp
      };

      if (gradedActivity.activityStatus === 'approved') {
        councilStats[`${activity.activityTypeId}-approved`] = firebase.default.firestore.FieldValue.increment(1);
        councilStats[`${activity.activityTypeId}-total-points`] = firebase.default.firestore.FieldValue.increment(activityPoints);
      }

      batch.set(councilGradingStatsDoc, councilStats, {merge: true});

      batch.commit().then(res => resolve(res), err => reject(err));
    });
  }

  updateGradedActivity(activity: Activity, gradedActivity: GradedActivity, originalGradedActivity: GradedActivity) {
    console.log('Updating Graded Activity');
    return new Promise<any>((resolve, reject) => {
      const batch = this.firestore.firestore.batch();
      const timestamp = this.timestamp;

      const activitiesDoc = this.firestore.firestore.collection(this.activitiesCollectionName).doc(gradedActivity.activityId);
      if(gradedActivity.activityStatus === 'reviewed'){
        // If this has only been reviewed, then it isn't actually graded
        batch.set(activitiesDoc, {graded: false}, {merge: true});
      } else {
        batch.set(activitiesDoc, {graded: true}, {merge: true});
      }

      const activityDataDoc = this.firestore.firestore.collection(this.activityDataCollectionName).doc(gradedActivity.activityId);

      // tslint:disable-next-line:max-line-length
      const activityPoints = (gradedActivity.activityStatus === 'rejected' || gradedActivity.activityStatus === 'reviewed') ? 0 : gradedActivity.isSupremeFeaturedProgram ? this.SUPREME_FEATURED_ACTIVITY_POINTS : this.ACTIVITY_POINTS;

      const activityData = {
        activityStatus: gradedActivity.activityStatus,
        activityPoints,
        isSupremeFeaturedProgram: gradedActivity.isSupremeFeaturedProgram,
        programDirectorComments: gradedActivity.programDirectorComments ?? '',
        isNotable: gradedActivity.isNotable,
        updatedAt: timestamp
      };

      batch.set(activityDataDoc, activityData, {merge: true});

      if (originalGradedActivity.activityStatus !== gradedActivity.activityStatus ||
          (gradedActivity.activityStatus === 'approved' &&
             originalGradedActivity.isSupremeFeaturedProgram !== gradedActivity.isSupremeFeaturedProgram)) {

        const council = this.councilService.getCouncilById(activity.councilId);
        const councilGradingStatsCol = this.firestore.firestore.collection(this.councilGradingStatsCollectionName);
        const councilGradingStatsDoc = councilGradingStatsCol.doc(`${council.id}`);

        const councilStats = {
          updatedAt: timestamp
        };

        if (originalGradedActivity.activityStatus !== gradedActivity.activityStatus) {
          if (gradedActivity.activityStatus === 'approved') {
            councilStats[`${activity.activityTypeId}-approved`] = firebase.default.firestore.FieldValue.increment(1);
            councilStats[`${activity.activityTypeId}-total-points`] = firebase.default.firestore.FieldValue.increment(activityPoints);
          } else {
            councilStats[`${activity.activityTypeId}-approved`] = firebase.default.firestore.FieldValue.increment(-1);
            councilStats[`${activity.activityTypeId}-total-points`] =
                  firebase.default.firestore.FieldValue
                      .increment(-1 * (originalGradedActivity.activityPoints ? originalGradedActivity.activityPoints : 0));
          }
        } else {
          // Supreme Featured Program was changed and the status is 'approved'
          councilStats[`${activity.activityTypeId}-total-points`]
              = firebase.default.firestore.FieldValue.increment(activityPoints - originalGradedActivity.activityPoints);
        }

        batch.set(councilGradingStatsDoc, councilStats, {merge: true});
      }

      batch.commit().then(res => resolve(res), err => reject(err));
    });
  }
}

export interface Activity {
  councilId: number;
  submitterName: string;
  submitterEmail: string;
  submitterPhone: string;
  programGroupId: string;
  activityTypeId: string;
  activityDate: Date;
  activityTitle: string;
  activityBriefDescription?: string;
  activityDescription?: string;
  numberOfKnights?: string;
  totalKnightHours?: string;
}

export interface GradedActivity {
  activityId: string;
  activityStatus: string;
  activityPoints?: number;
  programDirectorComments: string;
  isNotable: boolean;
  isSupremeFeaturedProgram: boolean;
}
